import { useEffect, useMemo, useRef, useState } from "react";
import { useCases } from "@/hooks/useCases";
import { getFlag, getISO } from "@/lib/countryIndex";
import { useTranslatedTexts } from "@/hooks/useTranslatedTexts";
import { useT } from "@/lib/i18n";
import countriesGeo from "@/data/countries.geo.json";

const ALL_WORLD_COUNTRIES: string[] = Array.from(
  new Set(
    ((countriesGeo as any).features as Array<{ properties: { ADMIN: string | null } }>)
      .map((f) => f.properties.ADMIN)
      .filter((n): n is string => !!n)
  )
).sort();

/**
 * Travel risk tool — stepped flow: country → Q1 → Q2 → AI analyzing → reveal.
 *
 * Risk model:
 * 1. Endemic baseline (CDC/ECDC/PAHO/WHO country profiles)
 * 2. Active outbreak cases (live useCases)
 * 3. Last 14 days activity
 * 4. Travel type (rural stay, rodent exposure)
 *
 * Score 0–100. Bands: 0-9 None / 10-29 Low / 30-54 Moderate / 55-79 High / 80+ Very high.
 */

type EndemicEntry = {
  variants: string[];
  baseline: number;
  note: string;
  region: string;
};

const ENDEMIC: Record<string, EndemicEntry> = {
  "Argentina": { variants: ["ANDV"], baseline: 32, region: "Patagonia & northeast", note: "ANDV endemic; person-to-person transmission documented." },
  "Chile": { variants: ["ANDV"], baseline: 38, region: "Aysén, Los Lagos, Bío Bío", note: "ANDV: 50–80 cases per year; CFR ~30%." },
  "Brazil": { variants: ["ANDV"], baseline: 22, region: "Southern & central-western rural areas", note: "Around 100+ HCPS cases reported annually." },
  "Paraguay": { variants: ["ANDV"], baseline: 18, region: "Chaco region", note: "Sporadic HCPS." },
  "Uruguay": { variants: ["ANDV"], baseline: 14, region: "Rural areas", note: "Low-frequency HCPS." },
  "Bolivia": { variants: ["ANDV"], baseline: 14, region: "Eastern lowlands", note: "Sporadic HCPS." },
  "Panama": { variants: ["Choclo"], baseline: 18, region: "Los Santos", note: "Choclo virus endemic; lower CFR." },
  "United States": { variants: ["SNV"], baseline: 16, region: "Four Corners (NM, AZ, CO, UT)", note: "SNV; ~30 HPS cases per year." },
  "Canada": { variants: ["SNV"], baseline: 10, region: "Western provinces (AB, SK, BC)", note: "SNV; few cases per year." },
  "Mexico": { variants: ["SNV"], baseline: 8, region: "Northern states", note: "Sporadic cases." },
  "Finland": { variants: ["PUUV"], baseline: 28, region: "Whole country", note: "Highest PUUV incidence in Europe (NE/HFRS, mild course)." },
  "Sweden": { variants: ["PUUV"], baseline: 20, region: "North", note: "PUUV (nephropathia epidemica)." },
  "Norway": { variants: ["PUUV"], baseline: 14, region: "Inland regions", note: "PUUV." },
  "Estonia": { variants: ["PUUV", "DOBV"], baseline: 16, region: "Rural", note: "PUUV and DOBV." },
  "Latvia": { variants: ["PUUV"], baseline: 12, region: "Rural", note: "PUUV." },
  "Germany": { variants: ["PUUV", "DOBV"], baseline: 16, region: "Baden-Württemberg, Bavaria", note: "PUUV outbreaks fluctuate with rodent years." },
  "France": { variants: ["PUUV"], baseline: 12, region: "North & east (Ardennes)", note: "PUUV; mild HFRS." },
  "Belgium": { variants: ["PUUV"], baseline: 12, region: "Ardennes", note: "PUUV." },
  "Netherlands": { variants: ["PUUV", "SEOV"], baseline: 10, region: "Eastern provinces", note: "PUUV and pet-rat-borne SEOV." },
  "Russia": { variants: ["PUUV", "DOBV", "HTNV"], baseline: 26, region: "Volga, Urals, Far East", note: "Largest HFRS burden in Europe." },
  "Slovakia": { variants: ["DOBV", "PUUV"], baseline: 10, region: "Rural", note: "Sporadic DOBV." },
  "Slovenia": { variants: ["DOBV", "PUUV"], baseline: 12, region: "Karst", note: "DOBV with high CFR." },
  "Croatia": { variants: ["DOBV", "PUUV"], baseline: 12, region: "Inland", note: "Periodic HFRS outbreaks." },
  "Bosnia and Herzegovina": { variants: ["DOBV", "PUUV"], baseline: 14, region: "Rural", note: "Periodic HFRS." },
  "Serbia": { variants: ["DOBV", "PUUV"], baseline: 14, region: "Rural", note: "Periodic HFRS, CFR ~10%." },
  "Greece": { variants: ["DOBV"], baseline: 10, region: "North", note: "DOBV; CFR can be high." },
  "Bulgaria": { variants: ["DOBV"], baseline: 10, region: "Rural", note: "DOBV." },
  "Romania": { variants: ["DOBV"], baseline: 8, region: "Rural", note: "Sporadic DOBV." },
  "United Kingdom": { variants: ["SEOV"], baseline: 6, region: "Pet-rat owners", note: "SEOV: wild- and pet-rat-borne, rare." },
  "China": { variants: ["HTNV", "SEOV"], baseline: 26, region: "Shaanxi, Heilongjiang, Liaoning", note: "Majority of global HFRS cases." },
  "South Korea": { variants: ["HTNV", "SEOV"], baseline: 18, region: "Rural & military training areas", note: "HTNV; vaccine available." },
  "North Korea": { variants: ["HTNV"], baseline: 14, region: "Rural", note: "HTNV." },
  "Japan": { variants: ["SEOV"], baseline: 6, region: "Port cities (historic)", note: "Rare in modern times." },
};

// Non-country labels that may appear in live cases — must NOT appear as travel destinations.
const NON_COUNTRY_RE = /^(at sea|unknown|international waters|open sea|n\/a)$/i;
const isRealCountry = (name: string) => !!name && !NON_COUNTRY_RE.test(name.trim());

type Risk = {
  level: "none" | "low" | "moderate" | "high" | "very-high";
  score: number;
  labelKey: "very-high" | "high" | "moderate" | "low" | "none";
  color: string;
  bg: string;
  border: string;
  emoji: string;
};

function scoreToRisk(score: number): Risk {
  if (score >= 80) return { level: "very-high", score, labelKey: "very-high", color: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.5)", emoji: "🚨" };
  if (score >= 55) return { level: "high", score, labelKey: "high", color: "#f97316", bg: "rgba(249,115,22,0.12)", border: "rgba(249,115,22,0.5)", emoji: "⚠️" };
  if (score >= 30) return { level: "moderate", score, labelKey: "moderate", color: "#eab308", bg: "rgba(234,179,8,0.12)", border: "rgba(234,179,8,0.5)", emoji: "⚡" };
  if (score >= 10) return { level: "low", score, labelKey: "low", color: "#22c55e", bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.5)", emoji: "✓" };
  return { level: "none", score, labelKey: "none", color: "var(--muted-foreground)", bg: "var(--card)", border: "var(--border)", emoji: "🌿" };
}

const STRINGS = [
  /* 0  */ "Check the hantavirus risk first.",
  /* 1  */ "AI-powered hantavirus risk briefing for your destination — built from the live outbreak feed. For informational use only; always follow official health authorities.",
  /* 2  */ "Check my risk",
  /* 3  */ "Pick your destination",
  /* 4  */ "Destination country",
  /* 5  */ "Search country...",
  /* 6  */ "Where are you staying?",
  /* 7  */ "City hotel / urban",
  /* 8  */ "Rural cabin, farm or camping",
  /* 9  */ "Will you be near rodents or old shelters?",
  /* 10 */ "No, just normal travel",
  /* 11 */ "Possibly — barns, attics, dust, droppings",
  /* 12 */ "AI is reading the live outbreak feed...",
  /* 13 */ "Cross-checking your travel profile...",
  /* 14 */ "Building your personalised briefing...",
  /* 15 */ "Risk score",
  /* 16 */ "Active outbreak",
  /* 17 */ "No cases in this country in the MV Hondius outbreak.",
  /* 18 */ "Deaths",
  /* 19 */ "Confirmed",
  /* 20 */ "Suspected",
  /* 21 */ "Last 14d:",
  /* 22 */ "new",
  /* 23 */ "AI analysis",
  /* 24 */ "Score breakdown",
  /* 25 */ "No known endemic circulation or active outbreak.",
  /* 26 */ "Endemic baseline",
  /* 27 */ "Active outbreak cases",
  /* 28 */ "Last 14 days activity",
  /* 29 */ "Rural / mountain stay",
  /* 30 */ "Possible rodent exposure",
  /* 31 */ "Very High",
  /* 32 */ "High",
  /* 33 */ "Moderate",
  /* 34 */ "Low",
  /* 35 */ "Very Low",
  /* 36 */ "Close",
  /* 37 */ "Back",
  /* 38 */ "Continue",
  /* 39 */ "Reveal my risk",
  /* 40 */ "Step",
  /* 41 */ "of",
  /* 42 */ "Pick a country from the list to continue.",
  /* 43 */ "Important: this tool is informational only. Always follow guidance from local health authorities (WHO, CDC, your national ministry of health). It cannot replace medical advice.",
  /* 44 */ "Start over",
  /* 45 */ "AI briefing unavailable right now. The risk score above is still based on live outbreak data.",
];

type Step = "country" | "stay" | "exposure" | "loading" | "result";

export default function TravelRiskTool() {
  const { lang } = useT();
  const T = useTranslatedTexts(STRINGS, lang);
  const { cases } = useCases();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("country");
  const [country, setCountry] = useState("");
  const [ruralStay, setRuralStay] = useState<boolean | null>(null);
  const [rodentContact, setRodentContact] = useState<boolean | null>(null);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [aiAnalysis, setAiAnalysis] = useState<string>("");
  const [aiError, setAiError] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);

  const allCountries = useMemo(() => {
    const set = new Set<string>(ALL_WORLD_COUNTRIES);
    Object.keys(ENDEMIC).forEach((c) => set.add(c));
    cases.forEach((c) => {
      if (isRealCountry(c.country)) set.add(c.country);
    });
    return Array.from(set).sort();
  }, [cases]);

  const assessment = useMemo(() => {
    if (!country) return null;
    const endemic = ENDEMIC[country];
    const local = cases.filter((c) => c.country === country);
    const fatal = local.filter((c) => c.status === "fatal").length;
    const confirmed = local.filter((c) => c.status === "confirmed").length;
    const suspected = local.filter((c) => c.status === "suspected").length;
    const now = Date.now();
    const recent = local.filter((c) => {
      if (!c.onsetDate) return false;
      return now - new Date(c.onsetDate).getTime() < 14 * 86400000;
    }).length;

    let score = 0;
    const factors: { labelIdx: number; value: string }[] = [];
    if (endemic) {
      score += endemic.baseline;
      factors.push({ labelIdx: 26, value: `+${endemic.baseline} (${endemic.variants.join(", ")})` });
    }
    const outbreakWeight = fatal * 12 + confirmed * 6 + suspected * 2;
    if (outbreakWeight > 0) {
      score += Math.min(outbreakWeight, 40);
      factors.push({ labelIdx: 27, value: `+${Math.min(outbreakWeight, 40)} (${fatal}† ${confirmed}✓ ${suspected}?)` });
    }
    if (recent > 0) {
      const w = Math.min(recent * 5, 15);
      score += w;
      factors.push({ labelIdx: 28, value: `+${w} (${recent} ${T[22]})` });
    }
    if (ruralStay) {
      score += 8;
      factors.push({ labelIdx: 29, value: "+8" });
    }
    if (rodentContact) {
      score += 12;
      factors.push({ labelIdx: 30, value: "+12" });
    }
    score = Math.min(100, Math.round(score));
    return {
      risk: scoreToRisk(score),
      endemic,
      local: { fatal, confirmed, suspected, total: local.length, recent, items: local },
      factors,
    };
  }, [country, cases, ruralStay, rodentContact, T]);

  // Score easing (only when result is shown)
  useEffect(() => {
    if (step !== "result") return;
    const target = assessment?.risk.score ?? 0;
    if (target === animatedScore) return;
    let raf: number;
    const start = performance.now();
    const from = animatedScore;
    const dur = 900;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimatedScore(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, assessment?.risk.score]);

  // Loading phases (cycle through 3 messages)
  useEffect(() => {
    if (step !== "loading") return;
    setLoadingPhase(0);
    const id = setInterval(() => setLoadingPhase((p) => (p + 1) % 3), 1100);
    return () => clearInterval(id);
  }, [step]);

  // Trigger AI call when entering loading; advance to result after both done.
  useEffect(() => {
    if (step !== "loading" || !assessment) return;
    let cancelled = false;
    setAiAnalysis("");
    setAiError(false);
    const startedAt = Date.now();

    const minDelay = new Promise((r) => setTimeout(r, 2800));

    const fetchAI = (async () => {
      try {
        const riskLabelEn =
          assessment.risk.labelKey === "very-high" ? "Very High" :
          assessment.risk.labelKey === "high" ? "High" :
          assessment.risk.labelKey === "moderate" ? "Moderate" :
          assessment.risk.labelKey === "low" ? "Low" : "Very Low";
        const res = await fetch("/api/travel-risk-ai", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country,
            language: lang,
            riskScore: assessment.risk.score,
            riskLevel: riskLabelEn,
            ruralStay: !!ruralStay,
            rodentContact: !!rodentContact,
            cases: assessment.local.items.slice(0, 30).map((c) => ({
              status: c.status,
              city: (c as any).city ?? undefined,
              onsetDate: c.onsetDate ?? undefined,
              notes: (c as any).notes ?? undefined,
            })),
          }),
        });
        if (!res.ok) throw new Error("ai");
        const data = await res.json();
        return typeof data.analysis === "string" ? data.analysis.trim() : "";
      } catch {
        return "";
      }
    })();

    Promise.all([minDelay, fetchAI]).then(([, analysis]) => {
      if (cancelled) return;
      if (analysis) setAiAnalysis(analysis);
      else setAiError(true);
      setStep("result");
      // Make total feel "weighty" if AI was super fast
      void startedAt;
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setStep("country");
    setCountry("");
    setRuralStay(null);
    setRodentContact(null);
    setAnimatedScore(0);
    setAiAnalysis("");
    setAiError(false);
  };

  const closeAll = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const riskLabel = (key: Risk["labelKey"]) => {
    switch (key) {
      case "very-high": return T[31];
      case "high": return T[32];
      case "moderate": return T[33];
      case "low": return T[34];
      case "none": return T[35];
    }
  };

  const stepIndex =
    step === "country" ? 1 :
    step === "stay" ? 2 :
    step === "exposure" ? 3 :
    step === "loading" ? 4 : 4;
  const totalSteps = 3;

  const isCountryValid = country && allCountries.includes(country);

  return (
    <>
      <section
        aria-label="Travel risk tool"
        className="border-t border-border bg-background text-foreground relative overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.07]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[300%]">
            <FlightPaths />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center relative">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-data mb-3 inline-flex items-center gap-2">
            <PlaneIcon className="w-3 h-3 animate-[plane-cruise_8s_ease-in-out_infinite]" />
            Travel Risk Tool
          </div>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05] mb-4">
            {T[0]}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-lg mx-auto mb-8">
            {T[1]}
          </p>

          <button
            onClick={() => setOpen(true)}
            className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-foreground text-background font-data text-sm uppercase tracking-wider font-bold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_10px_40px_-10px_rgba(239,68,68,0.5)] active:scale-100"
          >
            <span className="relative w-5 h-5">
              <span className="absolute inset-0 rounded-full bg-[#ef4444] opacity-30 animate-ping" />
              <span className="relative w-5 h-5 rounded-full bg-[#ef4444] flex items-center justify-center">
                <PlaneIcon className="w-3 h-3 text-background" />
              </span>
            </span>
            {T[2]}
            <ArrowIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-3 md:p-6 animate-fade-in"
          onClick={closeAll}
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-background/80 backdrop-blur-md" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl animate-scale-in"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 md:px-6 py-4 border-b border-border bg-card/95 backdrop-blur">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="relative w-7 h-7 rounded-full bg-[#ef4444]/10 flex items-center justify-center shrink-0">
                  <PlaneIcon className="w-3.5 h-3.5 text-[#ef4444]" />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-data leading-none">
                    Travel Risk
                  </div>
                  <div className="text-sm font-semibold text-foreground leading-tight mt-0.5 truncate">
                    {step === "result"
                      ? country
                      : step === "loading"
                      ? T[12]
                      : T[3]}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {step !== "result" && step !== "loading" && (
                  <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-muted-foreground font-data">
                    {T[40]} {stepIndex} {T[41]} {totalSteps}
                  </span>
                )}
                <button
                  onClick={closeAll}
                  aria-label={T[36]}
                  className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition"
                >
                  <CloseIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Step progress bar */}
            {step !== "result" && (
              <div className="h-0.5 bg-border/50 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-foreground transition-[width] duration-500 ease-out"
                  style={{ width: `${(stepIndex / totalSteps) * 100}%` }}
                />
              </div>
            )}

            {/* Body */}
            <div className="px-5 md:px-6 py-6">
              {step === "country" && (
                <div className="animate-fade-in space-y-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-data block mb-2">
                      {T[4]}
                    </label>
                    <CountryPicker
                      value={country}
                      onChange={setCountry}
                      options={allCountries}
                      placeholder={T[5]}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">{T[42]}</p>
                  <div className="pt-2 flex justify-end">
                    <PrimaryBtn
                      disabled={!isCountryValid}
                      onClick={() => setStep("stay")}
                    >
                      {T[38]}
                      <ArrowIcon className="w-3.5 h-3.5 ml-1.5" />
                    </PrimaryBtn>
                  </div>
                </div>
              )}

              {step === "stay" && (
                <div className="animate-fade-in space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">🏨</span>
                    {T[6]}
                  </div>
                  <div className="grid gap-2">
                    <BigChoice
                      icon="🏙️"
                      label={T[7]}
                      onClick={() => { setRuralStay(false); setStep("exposure"); }}
                    />
                    <BigChoice
                      icon="🏕️"
                      label={T[8]}
                      onClick={() => { setRuralStay(true); setStep("exposure"); }}
                    />
                  </div>
                  <div className="pt-2 flex justify-between">
                    <GhostBtn onClick={() => setStep("country")}>
                      <ArrowIcon className="w-3.5 h-3.5 mr-1.5 rotate-180" />
                      {T[37]}
                    </GhostBtn>
                  </div>
                </div>
              )}

              {step === "exposure" && (
                <div className="animate-fade-in space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">🐭</span>
                    {T[9]}
                  </div>
                  <div className="grid gap-2">
                    <BigChoice
                      icon="✓"
                      label={T[10]}
                      onClick={() => { setRodentContact(false); setStep("loading"); }}
                    />
                    <BigChoice
                      icon="⚠️"
                      label={T[11]}
                      onClick={() => { setRodentContact(true); setStep("loading"); }}
                    />
                  </div>
                  <div className="pt-2 flex justify-between">
                    <GhostBtn onClick={() => setStep("stay")}>
                      <ArrowIcon className="w-3.5 h-3.5 mr-1.5 rotate-180" />
                      {T[37]}
                    </GhostBtn>
                  </div>
                </div>
              )}

              {step === "loading" && (
                <div className="py-12 flex flex-col items-center text-center animate-fade-in">
                  <div className="relative w-16 h-16 mb-6">
                    <div className="absolute inset-0 rounded-full border-2 border-border" />
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#ef4444] animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PlaneIcon className="w-5 h-5 text-foreground animate-[plane-cruise_2s_ease-in-out_infinite]" />
                    </div>
                  </div>
                  <div className="space-y-2 min-h-[3.5rem]">
                    {[T[12], T[13], T[14]].map((msg, i) => (
                      <div
                        key={i}
                        className={`text-sm transition-all duration-500 ${
                          loadingPhase === i
                            ? "text-foreground opacity-100 translate-y-0"
                            : "text-muted-foreground opacity-50 translate-y-1"
                        }`}
                      >
                        {msg}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === "result" && assessment && (
                <ResultBlock
                  country={country}
                  risk={assessment.risk}
                  endemic={assessment.endemic}
                  local={assessment.local}
                  factors={assessment.factors}
                  animatedScore={animatedScore}
                  aiAnalysis={aiAnalysis}
                  aiError={aiError}
                  T={T}
                  riskLabel={riskLabel(assessment.risk.labelKey)}
                  onRestart={reset}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── UI atoms ─────────────────────────────────────────────────────────────

function PrimaryBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center px-5 py-2.5 rounded-full bg-foreground text-background text-xs uppercase tracking-wider font-data font-bold transition disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.03] active:scale-100"
    >
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center px-3 py-2 rounded-full text-xs uppercase tracking-wider font-data text-muted-foreground hover:text-foreground transition"
    >
      {children}
    </button>
  );
}

function BigChoice({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 w-full text-left px-4 py-4 rounded-xl border border-border bg-card/40 hover:border-foreground hover:bg-foreground/5 transition-all duration-200 hover:translate-x-0.5"
    >
      <span className="text-2xl shrink-0 transition-transform group-hover:scale-110">{icon}</span>
      <span className="flex-1 text-sm font-medium text-foreground">{label}</span>
      <ArrowIcon className="w-4 h-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition" />
    </button>
  );
}

function CountryPicker({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const matches = useMemo(() => {
    if (!focused) return [];
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 8);
    return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 8);
  }, [query, options, focused]);

  const flag = value ? getFlag(value) : "";
  const iso = value ? getISO(value) : "";

  return (
    <div className="relative">
      <div className="relative">
        {flag && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl pointer-events-none animate-scale-in">
            {flag}
          </span>
        )}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            const exact = options.find((o) => o.toLowerCase() === e.target.value.toLowerCase());
            if (exact) onChange(exact);
            else onChange("");
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          maxLength={80}
          autoComplete="off"
          className={`w-full bg-background border border-border rounded-lg py-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground/50 transition ${
            flag ? "pl-12" : "pl-3"
          }`}
        />
        {iso && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-data uppercase tracking-wider pointer-events-none">
            {iso}
          </span>
        )}
      </div>

      {focused && matches.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1.5 max-h-60 overflow-y-auto bg-card border border-border rounded-lg shadow-2xl animate-fade-in">
          {matches.map((m) => (
            <li key={m}>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(m);
                  setQuery(m);
                  inputRef.current?.blur();
                }}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-foreground/5 transition"
              >
                <span className="text-lg leading-none">{getFlag(m)}</span>
                <span className="flex-1 truncate text-foreground">{m}</span>
                <span className="text-[10px] text-muted-foreground font-data">{getISO(m)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ResultBlock({
  country,
  risk,
  endemic,
  local,
  factors,
  animatedScore,
  aiAnalysis,
  aiError,
  T,
  riskLabel,
  onRestart,
}: {
  country: string;
  risk: Risk;
  endemic?: EndemicEntry;
  local: { fatal: number; confirmed: number; suspected: number; total: number; recent: number };
  factors: { labelIdx: number; value: string }[];
  animatedScore: number;
  aiAnalysis: string;
  aiError: boolean;
  T: string[];
  riskLabel: string;
  onRestart: () => void;
}) {
  const flag = getFlag(country);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Risk hero */}
      <div
        className="rounded-xl border p-5 relative overflow-hidden"
        style={{ background: risk.bg, borderColor: risk.border }}
      >
        <div className="flex items-start justify-between gap-3 relative">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-data flex items-center gap-1.5">
              <span className="text-base leading-none animate-scale-in">{flag}</span>
              <span className="truncate">{country}</span>
            </div>
            {endemic && (
              <div className="text-xs text-muted-foreground mt-1 leading-snug">
                {endemic.region} · {endemic.variants.join(", ")}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl leading-none animate-[float_3s_ease-in-out_infinite]">{risk.emoji}</div>
            <div className="text-[10px] uppercase tracking-wider font-data font-bold mt-1" style={{ color: risk.color }}>
              {riskLabel}
            </div>
          </div>
        </div>

        <div className="mt-4 relative">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-data">{T[15]}</span>
            <span className="text-2xl font-bold font-data tabular-nums leading-none" style={{ color: risk.color }}>
              {animatedScore}
              <span className="text-xs text-muted-foreground font-normal ml-0.5">/100</span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-background/60 overflow-hidden relative">
            <div
              className="h-full rounded-full transition-[width] duration-700 ease-out relative"
              style={{ width: `${risk.score}%`, background: risk.color }}
            >
              <span
                className="absolute inset-0 rounded-full opacity-50"
                style={{
                  background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`,
                  animation: "shimmer 2s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* AI analysis */}
      <div
        className="rounded-xl border border-border bg-gradient-to-br from-card to-card/40 p-5 animate-fade-in relative overflow-hidden"
        style={{ animationDelay: "100ms", animationFillMode: "backwards" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="relative flex w-6 h-6 rounded-full bg-foreground/10 items-center justify-center">
            <SparkleIcon className="w-3.5 h-3.5 text-foreground" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-data text-foreground font-bold">
            {T[23]}
          </span>
        </div>
        {aiError ? (
          <p className="text-xs text-muted-foreground italic">{T[45]}</p>
        ) : (
          <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
            {aiAnalysis}
          </div>
        )}
      </div>

      {/* Live cases + factors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          className="rounded-lg border border-border bg-card/40 p-4 animate-fade-in"
          style={{ animationDelay: "150ms", animationFillMode: "backwards" }}
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-data mb-3 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />
            {T[16]}
          </div>
          {local.total === 0 ? (
            <p className="text-xs text-muted-foreground">{T[17]}</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <Mini value={local.fatal} label={T[18]} color="#ef4444" />
              <Mini value={local.confirmed} label={T[19]} color="#f97316" />
              <Mini value={local.suspected} label={T[20]} color="#eab308" />
            </div>
          )}
          {local.recent > 0 && (
            <div className="text-[11px] text-muted-foreground mt-3 font-data">
              {T[21]} <span className="text-foreground font-semibold">{local.recent}</span> {T[22]}
            </div>
          )}
        </div>

        <div
          className="rounded-lg border border-border bg-card/40 p-4 animate-fade-in"
          style={{ animationDelay: "200ms", animationFillMode: "backwards" }}
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-data mb-3">
            {T[24]}
          </div>
          {factors.length === 0 ? (
            <p className="text-xs text-muted-foreground">{T[25]}</p>
          ) : (
            <ul className="space-y-1.5">
              {factors.map((f, i) => (
                <li
                  key={f.labelIdx}
                  className="flex items-baseline justify-between gap-3 text-xs animate-fade-in"
                  style={{ animationDelay: `${250 + i * 60}ms`, animationFillMode: "backwards" }}
                >
                  <span className="text-muted-foreground truncate">{T[f.labelIdx]}</span>
                  <span className="text-foreground font-data tabular-nums">{f.value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div
        className="rounded-lg border border-border bg-card/40 p-4 animate-fade-in flex gap-2.5"
        style={{ animationDelay: "300ms", animationFillMode: "backwards" }}
      >
        <span className="text-base shrink-0" aria-hidden>ℹ️</span>
        <p className="text-[11px] text-muted-foreground leading-relaxed">{T[43]}</p>
      </div>

      {/* Restart */}
      <div className="flex justify-center pt-1">
        <button
          onClick={onRestart}
          className="text-[10px] uppercase tracking-wider font-data text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1.5"
        >
          <ArrowIcon className="w-3 h-3 rotate-180" />
          {T[44]}
        </button>
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes plane-cruise {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          50% { transform: translateX(3px) rotate(8deg); }
        }
      `}</style>
    </div>
  );
}

function Mini({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="border-l-2 pl-2" style={{ borderColor: color }}>
      <div className="text-lg font-semibold font-data tabular-nums text-foreground leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────

function PlaneIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1L15 22v-1.5L13 19v-5.5l8 2.5z" />
    </svg>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className} aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className} aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2zm7 11l.9 2.6L22 16.5l-2.1.9L19 20l-.9-2.6L16 16.5l2.1-.9L19 13z" />
    </svg>
  );
}

function FlightPaths() {
  return (
    <svg viewBox="0 0 800 400" className="w-full h-full" aria-hidden>
      <defs>
        <linearGradient id="path-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[
        "M 50 300 Q 400 50 750 280",
        "M 50 200 Q 400 380 750 180",
        "M 100 350 Q 450 100 700 350",
        "M 0 100 Q 400 250 800 80",
      ].map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="url(#path-grad)"
          strokeWidth="1.5"
          strokeDasharray="6 6"
          style={{ animation: `dash 30s linear infinite`, animationDelay: `${i * -3}s` }}
        />
      ))}
      <style>{`@keyframes dash { to { stroke-dashoffset: -200; } }`}</style>
    </svg>
  );
}
